/**
 * The What's Wrong? content as the Marketing pages show it: each episode's words
 * and links from the episode data itself (@shared-ui/whats-wrong — the same
 * source the renderer and the student page use), and its files from the
 * backend's listing of the `marketing` container.
 *
 * docs/FeatureDesigns/marketing/marketing-console-target.md
 */
import { EPISODES, SERIES_URL, landing, type WWEpisode } from '@shared-ui/whats-wrong/episodes.js';
import type { MarketingAsset } from '../../api/marketingApi';

export const SERIES = { id: 'whats-wrong', title: "What's wrong?" };

export type Format = MarketingAsset['format'];
export const FORMAT_LABELS: Record<Format, string> = {
  feed: 'Post 4:5',
  feed34: 'Post 3:4',
  story: 'Story 9:16',
  reel: 'Reel 9:16',
  short: 'YouTube Short 9:16',
};
export const FORMATS: Format[] = ['reel', 'feed', 'feed34', 'story', 'short'];

export interface EpisodeContent {
  episode: WWEpisode;
  files: MarketingAsset[];
  /** The tagged links to this episode's page, by where they are posted. */
  links: { post: string; ad: string; story: string };
}

/** The story link: the post link, its `utm_content` marked as the story's. */
function storyLink(ep: WWEpisode): string {
  return landing(ep, 'post').replace(`utm_content=ww${ep.id}`, `utm_content=ww${ep.id}-story`);
}

export function episodesWithFiles(files: MarketingAsset[]): EpisodeContent[] {
  return EPISODES.map(episode => ({
    episode,
    files: files.filter(f => f.episode === episode.id),
    links: { post: landing(episode, 'post'), ad: landing(episode, 'ad'), story: storyLink(episode) },
  }));
}

/** The shared Card C for a format (the third slide of every post and story). */
export function cardC(files: MarketingAsset[], format: Format): MarketingAsset | undefined {
  return files.find(f => f.episode === null && f.kind === 'more' && f.format === format);
}

export function pick(files: MarketingAsset[], format: Format, ...kinds: MarketingAsset['kind'][]): MarketingAsset | undefined {
  for (const kind of kinds) {
    // Prefer the video where a kind has both a still and a video.
    const found = files.filter(f => f.format === format && f.kind === kind);
    const video = found.find(f => f.media === 'video');
    if (video || found[0]) return video ?? found[0];
  }
  return undefined;
}

export const seriesUrl = SERIES_URL;

export function sizeLabel(bytes: number): string {
  return bytes >= 1e6 ? `${(bytes / 1e6).toFixed(1)} MB` : `${Math.round(bytes / 1e3)} KB`;
}
